import { motion } from 'framer-motion';
import { User, Bot, BookOpen } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { cn } from '../utils/cn';

export interface ChatBubbleProps {
  role: 'user' | 'assistant';
  content: string;
  timestamp?: string;
  citations?: Array<{
    article_number: string;
    code_name: string;
    title: string;
  }>;
  isLoading?: boolean;
}

export function ChatBubble({
  role,
  content,
  timestamp,
  citations,
  isLoading,
}: ChatBubbleProps) {
  const isUser = role === 'user';

  // For assistant messages, render as a professional document
  if (!isUser && !isLoading) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex gap-3"
      >
        {/* Avatar */}
        <div className="flex-shrink-0 h-8 w-8 rounded-full flex items-center justify-center bg-teal-100 text-teal-600">
          <Bot className="h-4 w-4" />
        </div>

        {/* Professional Document Response */}
        <div className="flex-1 max-w-[90%]">
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
            {/* Document Content */}
            <div className="p-5">
              <div className="prose prose-sm max-w-none prose-headings:text-gray-900 prose-headings:font-semibold prose-p:text-gray-700 prose-p:leading-relaxed prose-li:text-gray-700 prose-strong:text-gray-900 prose-strong:font-semibold">
                <ReactMarkdown
                  components={{
                    h1: ({ children }) => (
                      <h1 className="text-lg font-bold text-gray-900 mb-3 pb-2 border-b border-gray-200">
                        {children}
                      </h1>
                    ),
                    h2: ({ children }) => (
                      <h2 className="text-base font-semibold text-gray-900 mt-4 mb-2">
                        {children}
                      </h2>
                    ),
                    h3: ({ children }) => (
                      <h3 className="text-sm font-semibold text-gray-800 mt-3 mb-1">
                        {children}
                      </h3>
                    ),
                    p: ({ children }) => (
                      <p className="text-sm text-gray-700 mb-3 leading-relaxed">
                        {children}
                      </p>
                    ),
                    ul: ({ children }) => (
                      <ul className="list-none space-y-2 my-3 pl-0">
                        {children}
                      </ul>
                    ),
                    li: ({ children }) => (
                      <li className="flex items-start gap-2 text-sm text-gray-700">
                        <span className="text-teal-500 mt-1.5 flex-shrink-0">•</span>
                        <span>{children}</span>
                      </li>
                    ),
                    ol: ({ children }) => (
                      <ol className="list-decimal list-inside space-y-2 my-3">
                        {children}
                      </ol>
                    ),
                    strong: ({ children }) => (
                      <strong className="font-semibold text-gray-900">{children}</strong>
                    ),
                    em: ({ children }) => (
                      <em className="italic text-gray-600">{children}</em>
                    ),
                    code: ({ children }) => (
                      <code className="px-1.5 py-0.5 bg-gray-100 text-teal-700 rounded text-xs font-mono">
                        {children}
                      </code>
                    ),
                    a: ({ href, children }) => (
                      <a href={href} className="text-teal-600 hover:text-teal-700 underline">
                        {children}
                      </a>
                    ),
                  }}
                >
                  {content}
                </ReactMarkdown>
              </div>
            </div>

            {/* References Section */}
            {citations && citations.length > 0 && (
              <div className="border-t border-gray-100 bg-gray-50 px-5 py-4">
                <div className="flex items-center gap-2 mb-3">
                  <BookOpen className="h-4 w-4 text-teal-600" />
                  <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
                    References
                  </h4>
                </div>
                <div className="space-y-2">
                  {citations.map((citation, index) => (
                    <div
                      key={index}
                      className="flex items-start gap-2 text-xs"
                    >
                      <span className="text-teal-600 font-medium min-w-[20px]">
                        [{index + 1}]
                      </span>
                      <div className="text-gray-600">
                        <span className="font-medium text-gray-800">
                          {citation.article_number}
                        </span>
                        {citation.code_name && (
                          <span className="text-gray-500"> ({citation.code_name})</span>
                        )}
                        {citation.title && (
                          <span className="italic"> — {citation.title}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Timestamp */}
          {timestamp && (
            <span className="text-xs text-gray-400 mt-2 block">{timestamp}</span>
          )}
        </div>
      </motion.div>
    );
  }

  // User message or loading state
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={cn('flex gap-3', isUser && 'flex-row-reverse')}
    >
      {/* Avatar */}
      <div
        className={cn(
          'flex-shrink-0 h-8 w-8 rounded-full flex items-center justify-center',
          isUser ? 'bg-teal-100 text-teal-600' : 'bg-gray-100 text-gray-600'
        )}
      >
        {isUser ? <User className="h-4 w-4" /> : <Bot className="h-4 w-4" />}
      </div>

      {/* Message */}
      <div className={cn('flex flex-col max-w-[80%]', isUser && 'items-end')}>
        <div
          className={cn(
            'px-4 py-2 rounded-2xl',
            isUser
              ? 'bg-teal-600 rounded-br-md'
              : 'bg-gray-100 text-gray-900 rounded-bl-md'
          )}
          style={isUser ? { color: '#ffffff' } : undefined}
        >
          {isLoading ? (
            <div className="flex items-center gap-1 py-1">
              <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" />
              <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }} />
              <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }} />
            </div>
          ) : (
            <p className="whitespace-pre-wrap text-sm">{content}</p>
          )}
        </div>

        {/* Timestamp */}
        {timestamp && (
          <span className="text-xs text-gray-400 mt-1">{timestamp}</span>
        )}
      </div>
    </motion.div>
  );
}
